import { z } from 'zod';

import { isPasswordStrong } from './password-policy.js';

// API contracts shared between backend (controllers + DTOs) and frontend
// (forms + clients). Adding a field here automatically widens both sides.

const StrongPassword = z.string().refine(isPasswordStrong, {
  message: 'password rules failed',
});

export const LoginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
  deviceId: z.string().optional().nullable(),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const ActivateInvitationRequestSchema = z.object({
  invitationToken: z.string().min(16),
  password: StrongPassword,
});
export type ActivateInvitationRequest = z.infer<typeof ActivateInvitationRequestSchema>;

// `refreshToken` is optional in the body: bootstrap requests on page reload
// rely on the httpOnly `jq4_refresh` cookie (per ADR-010 we don't keep the
// refresh token in client-readable storage). Mid-session 401 retries still
// pass the in-memory token explicitly.
export const RefreshRequestSchema = z.object({
  refreshToken: z.string().min(16).optional(),
});
export type RefreshRequest = z.infer<typeof RefreshRequestSchema>;

export const LogoutRequestSchema = z.object({
  refreshToken: z.string().min(16),
});
export type LogoutRequest = z.infer<typeof LogoutRequestSchema>;

export const RequestPasswordResetRequestSchema = z.object({
  email: z.string().email(),
});
export type RequestPasswordResetRequest = z.infer<typeof RequestPasswordResetRequestSchema>;

export const ResetPasswordRequestSchema = z.object({
  resetToken: z.string().min(16),
  newPassword: StrongPassword,
});
export type ResetPasswordRequest = z.infer<typeof ResetPasswordRequestSchema>;

// MFA (JQ4-4)

export const SetupMfaRequestSchema = z.object({
  mfaToken: z.string().min(16),
});
export type SetupMfaRequest = z.infer<typeof SetupMfaRequestSchema>;

export const ActivateMfaRequestSchema = z.object({
  mfaToken: z.string().min(16),
  code: z.string().min(6).max(8),
});
export type ActivateMfaRequest = z.infer<typeof ActivateMfaRequestSchema>;

export const VerifyMfaRequestSchema = z.object({
  mfaToken: z.string().min(16),
  // 6-digit TOTP or 10-char base64url recovery code
  code: z.string().min(6).max(20),
});
export type VerifyMfaRequest = z.infer<typeof VerifyMfaRequestSchema>;

// Email OTP — alternative second factor (email-otp-2fa). Lets a user who
// skipped TOTP enrollment at first login still complete mandatory 2FA via a
// 6-digit code emailed to them.

export const RequestMfaEmailOtpRequestSchema = z.object({
  mfaToken: z.string().min(16),
});
export type RequestMfaEmailOtpRequest = z.infer<typeof RequestMfaEmailOtpRequestSchema>;

export const VerifyMfaEmailOtpRequestSchema = z.object({
  mfaToken: z.string().min(16),
  code: z.string().regex(/^\d{6}$/, 'code must be a 6-digit number'),
});
export type VerifyMfaEmailOtpRequest = z.infer<typeof VerifyMfaEmailOtpRequestSchema>;

// User invitation (JQ4-5)

export const CreateInvitationRequestSchema = z.object({
  email: z.string().email(),
  role: z.enum(['supervisor', 'operator']),
});
export type CreateInvitationRequest = z.infer<typeof CreateInvitationRequestSchema>;

export const CreateInvitationResponseSchema = z.object({
  invitationId: z.string().uuid(),
  email: z.string().email(),
  role: z.enum(['supervisor', 'operator']),
  expiresAt: z.string().datetime(),
});
export type CreateInvitationResponse = z.infer<typeof CreateInvitationResponseSchema>;

// REQ-C-2 — widened to include org_admin for the super-admin resend path.
// Further widened to include super_admin for the platform-team resend path
// (super_admin resending another super_admin's invitation). Both widenings are
// additive; the tenant resend endpoint (its own controller action) can never
// return org_admin/super_admin so existing consumers are unaffected.
export const ResendInvitationResponseSchema = z.object({
  invitationId: z.string().uuid(),
  email: z.string().email(),
  role: z.enum(['super_admin', 'org_admin', 'supervisor', 'operator']),
  expiresAt: z.string().datetime(),
});
export type ResendInvitationResponse = z.infer<typeof ResendInvitationResponseSchema>;

export const InvitationListItemSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  role: z.enum(['super_admin', 'org_admin', 'supervisor', 'operator']),
  status: z.enum(['pending', 'expired', 'used']),
  expiresAt: z.string().datetime(),
  createdAt: z.string().datetime(),
});
export type InvitationListItem = z.infer<typeof InvitationListItemSchema>;

export const ListInvitationsResponseSchema = z.object({
  invitations: z.array(InvitationListItemSchema),
});
export type ListInvitationsResponse = z.infer<typeof ListInvitationsResponseSchema>;

// ── Platform team — super_admin inviting/managing other super_admins ────────
// Role is implicit (super_admin) and organizationId is always null, so the
// request only carries an email; reuses InvitationListItemSchema for the list
// response (role will always be 'super_admin' in that slice).

export const InvitePlatformAdminRequestSchema = z.object({
  email: z.string().email(),
});
export type InvitePlatformAdminRequest = z.infer<typeof InvitePlatformAdminRequestSchema>;

// No `role` field — implicit super_admin, mirrors InviteFirstOrgAdminResponseSchema.
export const InvitePlatformAdminResponseSchema = z.object({
  invitationId: z.string().uuid(),
  email: z.string().email(),
  expiresAt: z.string().datetime(),
});
export type InvitePlatformAdminResponse = z.infer<typeof InvitePlatformAdminResponseSchema>;

export const PlatformSuperAdminListItemSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email().nullable(),
  active: z.boolean(),
  lastLoginAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type PlatformSuperAdminListItem = z.infer<typeof PlatformSuperAdminListItemSchema>;

export const PlatformSuperAdminListResponseSchema = z.object({
  members: z.array(PlatformSuperAdminListItemSchema),
});
export type PlatformSuperAdminListResponse = z.infer<typeof PlatformSuperAdminListResponseSchema>;

// super-admin-deactivate-users — PATCH /super-admin/team/members/:id toggles
// a platform super_admin between active/inactive (reversible, unlike the
// tenant-scoped DELETE /admin/users/:id which is deactivate-only).
export const SetPlatformAdminActiveRequestSchema = z.object({
  active: z.boolean(),
});
export type SetPlatformAdminActiveRequest = z.infer<typeof SetPlatformAdminActiveRequestSchema>;

export const SetPlatformAdminActiveResponseSchema = z.object({
  userId: z.string().uuid(),
  active: z.boolean(),
  sessionsRevoked: z.number().int().nonnegative(),
});
export type SetPlatformAdminActiveResponse = z.infer<typeof SetPlatformAdminActiveResponseSchema>;

// User management (JQ4-6)

const RoleEnum = z.enum(['super_admin', 'org_admin', 'supervisor', 'operator']);
const ManageableRoleEnum = z.enum(['supervisor', 'operator']);

export const ChangeUserRoleRequestSchema = z.object({
  role: ManageableRoleEnum,
});
export type ChangeUserRoleRequest = z.infer<typeof ChangeUserRoleRequestSchema>;

export const ChangeUserRoleSuperAdminRequestSchema = z.object({
  role: RoleEnum,
});
export type ChangeUserRoleSuperAdminRequest = z.infer<typeof ChangeUserRoleSuperAdminRequestSchema>;

export const ChangeUserRoleResponseSchema = z.object({
  userId: z.string().uuid(),
  organizationId: z.string().uuid().nullable(),
  previousRole: RoleEnum,
  newRole: RoleEnum,
});
export type ChangeUserRoleResponse = z.infer<typeof ChangeUserRoleResponseSchema>;

export const DeactivateUserResponseSchema = z.object({
  userId: z.string().uuid(),
  organizationId: z.string().uuid().nullable(),
  sessionsRevoked: z.number().int().nonnegative(),
});
export type DeactivateUserResponse = z.infer<typeof DeactivateUserResponseSchema>;

export const ReactivateUserResponseSchema = z.object({
  userId: z.string().uuid(),
  organizationId: z.string().uuid().nullable(),
});
export type ReactivateUserResponse = z.infer<typeof ReactivateUserResponseSchema>;

export const UserListItemSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email().nullable(),
  role: RoleEnum,
  active: z.boolean(),
  lastLoginAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type UserListItem = z.infer<typeof UserListItemSchema>;

export const ListUsersResponseSchema = z.object({
  users: z.array(UserListItemSchema),
  total: z.number().int().nonnegative(),
});
export type ListUsersResponse = z.infer<typeof ListUsersResponseSchema>;

export const ListUsersQueryParamsSchema = z.object({
  q: z.string().optional(),
  role: z.enum(['super_admin', 'org_admin', 'supervisor', 'operator']).optional(),
  page: z.number().int().min(1).optional(),
  limit: z.number().int().min(1).max(100).optional(),
});
export type ListUsersQueryParams = z.infer<typeof ListUsersQueryParamsSchema>;

// Session management (JQ4-8 · US-IAM-007)

export const SessionListItemSchema = z.object({
  id: z.string().uuid(),
  deviceId: z.string().nullable(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  issuedAt: z.string().datetime(),
  lastSeenAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
  isCurrent: z.boolean(),
});
export type SessionListItem = z.infer<typeof SessionListItemSchema>;

export const ListUserSessionsResponseSchema = z.object({
  sessions: z.array(SessionListItemSchema),
});
export type ListUserSessionsResponse = z.infer<typeof ListUserSessionsResponseSchema>;

export const RevokeAllSessionsResponseSchema = z.object({
  sessionsRevoked: z.number().int().nonnegative(),
});
export type RevokeAllSessionsResponse = z.infer<typeof RevokeAllSessionsResponseSchema>;

// API response shapes

export const LoginResponseSchema = z.discriminatedUnion('mfaPending', [
  z.object({
    mfaPending: z.literal(false),
    accessToken: z.string(),
    refreshToken: z.string(),
    expiresIn: z.number(),
  }),
  z.object({
    mfaPending: z.literal(true),
    mfaToken: z.string(),
    setupRequired: z.boolean(),
  }),
]);
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

export const RefreshResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number(),
  // The access JWT carries no email; the refresh response does so a reloaded
  // client can show who is signed in (QA R2-10). Optional for older servers.
  email: z.string().email().nullable().optional(),
});
export type RefreshResponse = z.infer<typeof RefreshResponseSchema>;

export const ActivateInvitationResponseSchema = z.object({
  userId: z.string().uuid(),
  // Null for platform-level invitations (super_admin activating another
  // super_admin — no organization scope).
  organizationId: z.string().uuid().nullable(),
  email: z.string().email(),
  mfaSetupRequired: z.boolean(),
  // Additive: role of the activated user (operator = valet -> PWA, not the admin console).
  role: z.enum(['super_admin', 'org_admin', 'supervisor', 'operator']).optional(),
});
export type ActivateInvitationResponse = z.infer<typeof ActivateInvitationResponseSchema>;

export const SetupMfaResponseSchema = z.object({
  otpauthUri: z.string(),
  secret: z.string(),
});
export type SetupMfaResponse = z.infer<typeof SetupMfaResponseSchema>;

export const ActivateMfaResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number(),
  recoveryCodes: z.array(z.string()),
});
export type ActivateMfaResponse = z.infer<typeof ActivateMfaResponseSchema>;

export const VerifyMfaResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number(),
});
export type VerifyMfaResponse = z.infer<typeof VerifyMfaResponseSchema>;

export const RequestMfaEmailOtpResponseSchema = z.object({
  sent: z.literal(true),
  expiresInMinutes: z.number(),
});
export type RequestMfaEmailOtpResponse = z.infer<typeof RequestMfaEmailOtpResponseSchema>;

// Same session shape as VerifyMfaResponseSchema — deliberately NOT reused by
// reference so each MFA path's contract can evolve independently (mirrors
// ActivateMfaResponseSchema's own copy rather than extending VerifyMfa's).
export const VerifyMfaEmailOtpResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number(),
});
export type VerifyMfaEmailOtpResponse = z.infer<typeof VerifyMfaEmailOtpResponseSchema>;
